/**
 * Clipboard store.
 *
 * Global (not workspace-scoped) history plus the opt-in background monitor.
 * `startMonitor`/`stopMonitor` are driven from `ClipboardPage` and
 * `AppLayout` by `settingsStore.clipboardMonitoringEnabled` — the monitor
 * never runs unless that's explicitly on. Reading the clipboard uses the
 * standard `navigator.clipboard.readText()` API (no new dependency); on
 * platforms/webviews that require focus or a permission prompt for silent
 * reads, a poll simply no-ops rather than throwing, so the feature degrades
 * gracefully instead of spamming errors.
 */
import { create } from "zustand";
import type { ClipboardItem } from "@/types";
import { clipboardRepository } from "@/services/storage/clipboardRepository";
import { logger } from "@/services/logger";
import { isTauri } from "@/utils";
import { useAppStore } from "./appStore";
import { useSettingsStore } from "./settingsStore";

const log = logger.scope("clipboard-store");
const POLL_INTERVAL_MS = 2000;

function isSensitive(content: string): boolean {
  const patterns = useSettingsStore.getState().clipboardSensitivePatterns;
  return patterns.some((source) => {
    try {
      return new RegExp(source).test(content);
    } catch {
      return false;
    }
  });
}

interface ClipboardState {
  items: ClipboardItem[];
  loaded: boolean;
  monitoring: boolean;

  init: () => Promise<void>;
  refresh: () => Promise<void>;
  setPinned: (id: string, pinned: boolean) => Promise<void>;
  remove: (id: string) => Promise<void>;
  clear: () => Promise<void>;
  startMonitor: () => void;
  stopMonitor: () => void;
}

let pollHandle: ReturnType<typeof setInterval> | null = null;
let lastSeenContent: string | null = null;

async function guarded(action: () => Promise<void>, failureTitle: string): Promise<void> {
  try {
    await action();
  } catch (err) {
    log.error(failureTitle, err);
    useAppStore.getState().addToast({
      title: failureTitle,
      description: err instanceof Error ? err.message : undefined,
      variant: "error",
    });
  }
}

export const useClipboardStore = create<ClipboardState>()((set, get) => ({
  items: [],
  loaded: false,
  monitoring: false,

  init: async () => {
    if (get().loaded || !isTauri()) {
      set({ loaded: true });
      return;
    }
    await get().refresh();
  },

  refresh: async () => {
    try {
      const items = await clipboardRepository.list();
      set({ items, loaded: true });
    } catch (err) {
      log.error("Failed to load clipboard history", err);
      set({ loaded: true });
    }
  },

  setPinned: async (id, pinned) => {
    await guarded(async () => {
      await clipboardRepository.setPinned(id, pinned);
      await get().refresh();
    }, "Couldn't update clipboard item");
  },

  remove: async (id) => {
    set((s) => ({ items: s.items.filter((i) => i.id !== id) }));
    await guarded(() => clipboardRepository.delete(id), "Couldn't delete clipboard item");
  },

  clear: async () => {
    await guarded(async () => {
      await clipboardRepository.clear();
      await get().refresh();
    }, "Couldn't clear clipboard history");
  },

  startMonitor: () => {
    if (pollHandle || !isTauri()) return;
    set({ monitoring: true });
    pollHandle = setInterval(() => {
      void (async () => {
        if (!navigator.clipboard?.readText) return;
        let text: string;
        try {
          text = await navigator.clipboard.readText();
        } catch {
          return; // No permission / window unfocused — silently skip this poll.
        }
        const trimmed = text.trim();
        if (!trimmed || trimmed === lastSeenContent) return;
        lastSeenContent = trimmed;
        if (isSensitive(trimmed) && useSettingsStore.getState().clipboardAutoCleanEnabled) return;
        const contentType = /^https?:\/\//.test(trimmed) ? "url" : /[{};]|function |const |import /.test(trimmed) ? "code" : "text";
        await clipboardRepository.add(trimmed, contentType);
        await get().refresh();
      })();
    }, POLL_INTERVAL_MS);
  },

  stopMonitor: () => {
    if (pollHandle) {
      clearInterval(pollHandle);
      pollHandle = null;
    }
    set({ monitoring: false });
  },
}));
